import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CAPABILITIES } from '../data/capabilities';
import { useExplore } from '../context/ExploreContext';
import { mediaUrl } from '../utils/mediaUrl';
import AnimatedSection from './AnimatedSection';
import './CapabilityExplorer.css';

const CapabilityExplorer = () => {
  const { markExplored, isExplored, exploredCount, pillarIds } = useExplore();
  const [activeId, setActiveId] = useState(CAPABILITIES[0].id);
  const reduceMotion = useReducedMotion();

  const active = CAPABILITIES.find((c) => c.id === activeId) || CAPABILITIES[0];

  const selectPillar = (id) => {
    setActiveId(id);
    markExplored(id);
  };

  return (
    <section className="explorer section" id="capabilities" aria-labelledby="explorer-title">
      <div className="container">
        <AnimatedSection>
          <p className="section-label">/ Capabilities</p>
          <h2 id="explorer-title" className="section-title">
            Explore what we own.
          </h2>
          <p className="section-subtitle">
            Click a pillar to explore. Progress saves on this device —
            unlock the full stack when you have opened all four.
          </p>
          <p className="explorer-progress" aria-live="polite">
            <span className="explorer-progress-count">
              {exploredCount} of {pillarIds.length}
            </span>{' '}
            explored
          </p>
        </AnimatedSection>

        <div className="explorer-layout">
          <div
            className="explorer-pillars"
            role="tablist"
            aria-label="Capability pillars"
          >
            {CAPABILITIES.map((cap) => {
              const explored = isExplored(cap.id);
              const selected = activeId === cap.id;
              return (
                <button
                  key={cap.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`explorer-pillar ${selected ? 'active' : ''} ${
                    explored ? 'explored' : ''
                  }`}
                  onClick={() => selectPillar(cap.id)}
                >
                  {selected && !reduceMotion && (
                    <motion.span
                      layoutId="pillar-indicator"
                      className="explorer-pillar-indicator"
                      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    />
                  )}
                  {selected && reduceMotion && (
                    <span className="explorer-pillar-indicator" />
                  )}
                  <span className="explorer-pillar-label">{cap.label}</span>
                  {explored && <span className="explorer-pillar-check" aria-hidden="true">✓</span>}
                </button>
              );
            })}
          </div>

          <div className="explorer-panel" role="tabpanel">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0, y: -8 }}
                transition={{ duration: 0.28 }}
              >
                <h3 className="explorer-panel-title">{active.label}</h3>
                <p className="explorer-panel-claim">{active.claim}</p>
                <div className="explorer-stack">
                  {active.stack.map((tech) => (
                    <span key={tech} className="chip">
                      {tech}
                    </span>
                  ))}
                </div>
                <div className="explorer-demos">
                  {active.demos.map((demo) => (
                    <div key={demo.id} className="explorer-demo">
                      {demo.isImage || demo.image ? (
                        <img
                          src={mediaUrl(demo.image)}
                          alt={demo.alt || demo.title}
                          loading="lazy"
                        />
                      ) : (
                        <video
                          muted
                          playsInline
                          preload="metadata"
                          src={mediaUrl(demo.video)}
                          onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                          onMouseLeave={(e) => {
                            e.currentTarget.pause();
                            e.currentTarget.currentTime = 0;
                          }}
                        />
                      )}
                      <span>{demo.title}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to={`/portfolio?filter=${active.portfolioFilter}`}
                  className="btn btn-outline explorer-cta"
                >
                  See in portfolio
                </Link>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CapabilityExplorer;
